import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Loader2, Mic, StopCircle, ImagePlus, ChevronLeft, Lock, HeartHandshake, MessageCircle, Mail, Share2 } from "lucide-react";
import { Ornament } from "../components/Ornament";
import { STORY_WEEK_DAYS, STORY_WEEK_CHAPTERS } from "../data/storyWeek";
import { saveDay, transcribeVoice, uploadPhoto, setAnonId } from "../lib/storyWeek";
import { RecordingTips } from "../components/RecordingTips";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { storytellerDisplayName } from "../lib/storyteller";

const DAY5_GENTLE_QUESTION = "Tell me about a small kindness — one you gave or one you received — that you've never forgotten.";

/** Format an HH:MM (24h) string as e.g. "8:00am". Falls back to the raw
 *  string if it can't be parsed. */
function formatDeliveryTime(hhmm) {
  if (!hhmm || typeof hhmm !== "string") return "8:00am";
  const [h, m] = hhmm.split(":").map((n) => parseInt(n, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return hhmm;
  const period = h >= 12 ? "pm" : "am";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${m.toString().padStart(2, "0")}${period}`;
}

/** Non-blocking email confirmation controls used inline on the Day 1 UI.
 *  Talks to POST /auth/resend-verification, which is now synchronous and
 *  returns { sent: true, email_id } only when Resend returns a real ID. */
function ResendConfirmationButton() {
  const [state, setState] = useState("idle"); // idle | sending | sent | error
  const [emailId, setEmailId] = useState("");
  const onClick = async () => {
    setState("sending");
    try {
      const r = await api.post("/auth/resend-verification", { origin_url: window.location.origin, flow: "story_week" });
      if (r.data?.email_id) {
        setEmailId(r.data.email_id);
        setState("sent");
      } else {
        setState("error");
      }
    } catch (e) {
      setState("error");
    }
  };
  return (
    <>
      <button
        type="button"
        onClick={onClick}
        disabled={state === "sending"}
        className="btn-primary w-full"
        style={{ minHeight: 52, padding: "14px 20px", fontSize: "15px" }}
        data-testid="q1-resend-confirmation-btn"
      >
        {state === "sending" ? "Sending…"
          : state === "sent" ? "Sent — please check your inbox again"
          : "Click here to resend my confirmation email"}
      </button>
      {state === "error" && (
        <p className="text-red-700 text-sm text-center" role="alert" data-testid="q1-resend-error">
          We couldn't send your email. Please try again.
        </p>
      )}
    </>
  );
}

function Day1TimePreference() {
  const { user, refresh } = useAuth();
  const [time, setTime] = useState(user?.delivery_time || "08:00");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  if (!user) return null;
  const onSave = async () => {
    setError(""); setSaving(true);
    try {
      await api.patch("/me/delivery", { delivery_time: time });
      if (refresh) await refresh();
      setSaved(true);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't save just now.");
    } finally {
      setSaving(false);
    }
  };
  return (
    <aside
      className="mt-12 mx-auto text-left hairline-cream bg-[#FAF7F2]"
      style={{ maxWidth: 480, padding: "24px 24px 20px", borderRadius: 4 }}
      data-testid="sw-day1-time-pref"
    >
      <p className="label-ui text-[10px] text-[#B8973A] tracking-[0.3em]" style={{ marginBottom: 8 }}>
        DAILY DELIVERY
      </p>
      <h2 className="text-2xl" style={{ fontFamily: "Newsreader, serif", fontWeight: 400, color: "#111D33", lineHeight: 1.2 }}>
        What time would you like tomorrow&rsquo;s question?
      </h2>
      <div className="mt-4 flex items-center gap-3">
        <input
          type="time" value={time}
          onChange={(e) => setTime(e.target.value)}
          className="px-4 py-3 hairline-cream bg-white text-[#1B2A4A]"
          style={{ borderRadius: 4, fontFamily: "'DM Sans', sans-serif", fontSize: "16px", minHeight: 44 }}
          data-testid="sw-day1-time-input"
        />
        <button
          type="button" onClick={onSave} disabled={saving}
          className="btn-gold" style={{ minHeight: 44, padding: "10px 20px", fontSize: "15px", letterSpacing: "0" }}
          data-testid="sw-day1-time-save"
        >
          {saving ? "Saving…" : saved ? "SaveD" : "Save TIME"}
        </button>
      </div>
      <p className="mt-3 text-[#8C8278]" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "14px" }}>
        {saved
          ? "Perfect. We'll write to you at this time tomorrow. You can change it any time in Delivery settings."
          : "Emails arrive at this local time in your timezone. WhatsApp coming soon."}
      </p>
      {error && <p className="mt-2 text-red-700 text-sm" role="alert">{error}</p>}
    </aside>
  );
}


/** Invite-only share module shown on the Day 1 "story saved" screen.
 *  Public story-sharing is DISABLED for launch — this ONLY sends a short
 *  generic invitation and the public Story Week landing URL. It never
 *  includes the user's name, answer, recording, transcript or photograph. */
function ShareDay1Section() {
  const APP_ORIGIN = (typeof window !== "undefined" ? window.location.origin : "");
  const INVITE_URL = APP_ORIGIN;
  const INVITE_MESSAGE =
    "I've just started Story Week with The Living Book — seven questions to begin recording the stories that matter. Would you like to begin yours?";
  const INVITE_WITH_URL = `${INVITE_MESSAGE}\n\n${INVITE_URL}`;

  const openWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(INVITE_WITH_URL)}`, "_blank", "noopener,noreferrer");
  };
  const openEmail = () => {
    window.location.href = `mailto:?subject=${encodeURIComponent("Begin Story Week with me")}&body=${encodeURIComponent(INVITE_WITH_URL)}`;
  };
  const openNative = async () => {
    if (!navigator.share) { openWhatsApp(); return; }
    try {
      await navigator.share({ title: "Story Week — The Living Book", text: INVITE_MESSAGE, url: INVITE_URL });
    } catch (e) { /* user cancelled */ }
  };

  const hasNativeShare = typeof navigator !== "undefined" && !!navigator.share;
  const rowBtn = "flex items-center justify-center gap-2 py-3 px-4 hairline text-[#1B2A4A] transition-colors hover:bg-[#F7F0E0]";
  const rowBtnStyle = { background: "white", borderColor: "#E8D5A3", borderRadius: 4, minHeight: 52, fontFamily: "'DM Sans', sans-serif", fontSize: "15px" };

  return (
    <div className="mt-2 text-left" data-testid="share-invite-section">
      <div className="p-5 sm:p-6" style={{ background: "#FFFDF8", border: "1px solid #E8D5A3", borderRadius: 4 }} data-testid="share-invite">
        <p className="label-ui text-[11px] text-[#B8973A] tracking-[0.25em] mb-2">INVITE SOMEONE TO START STORY WEEK</p>
        <p className="text-[#1B2A4A] mb-4" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "15px", lineHeight: 1.65 }}>
          Send a short invitation so someone you love can begin their own Story Week. Your own story stays private.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {hasNativeShare && (
            <button type="button" onClick={openNative} className="btn-gold flex items-center justify-center gap-2 sm:col-span-2" style={{ minHeight: 52, padding: "14px 20px", fontSize: "15px" }} data-testid="share-invite-native">
              <Share2 size={16} /> Share the invitation
            </button>
          )}
          <button type="button" onClick={openWhatsApp} className={rowBtn} style={rowBtnStyle} data-testid="share-invite-whatsapp">
            <MessageCircle size={16} color="#25D366" /> Invite on WhatsApp
          </button>
          <button type="button" onClick={openEmail} className={rowBtn} style={rowBtnStyle} data-testid="share-invite-email">
            <Mail size={16} color="#B8973A" /> Invite by email
          </button>
        </div>
      </div>
    </div>
  );
}

export default function StoryWeekDay() {
  const { day: dayParam } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const day = Number(dayParam);
  const dayData = STORY_WEEK_DAYS.find((d) => d.day === day);
  const isDay5 = day === 5;
  const stName = storytellerDisplayName(user);

  const [session, setSession] = useState(null);
  const [locked, setLocked] = useState(false);
  const [text, setText] = useState("");
  const [name, setName] = useState("");
  const [storyMode, setStoryMode] = useState("own");
  const [photoPath, setPhotoPath] = useState(null);
  const [audioPath, setAudioPath] = useState(null);
  const [saving, setSaving] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [error, setError] = useState("");
  const [showHelpers, setShowHelpers] = useState(false);
  // After saving, show an explicit confirmation screen ("Your first story has
  // been saved") instead of jumping silently to the next day. The user
  // navigates onward from that screen deliberately.
  const [justSaved, setJustSaved] = useState(false);
  const [savedNextUnlock, setSavedNextUnlock] = useState(null);
  // Autosave — background draft save every 5s while typing. Never sets the
  // "saved" status; only the Save button does that.
  const [autoState, setAutoState] = useState("idle"); // idle | saving | saved | error
  const lastSavedTextRef = useRef("");
  const draftDebounceRef = useRef(null);
  // Day 5 gentle-mode
  const [day5Choice, setDay5Choice] = useState(null); // null | "answer" | "gentle" | "skip"
  const fileInputRef = useRef(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate(`/login?flow=story_week&next=${encodeURIComponent(`/story-week/day/${day}`)}`, { replace: true }); return; }
    // Storyteller onboarding — PROMPT 4 PART 1. Signed-in users without a
    // storyteller are sent to the "Who will you be sitting down with?" step,
    // then to the intro screen, then back to Day 1.
    if (!user.storyteller) {
      navigate(`/onboarding/storyteller?next=${encodeURIComponent(`/story-week/intro`)}`, { replace: true });
      return;
    }
    if (day === 1 && !user.intro_seen_at) {
      navigate("/story-week/intro", { replace: true });
      return;
    }
    // Day 1 is always open for a signed-in user — the confirmation email is
    // sent in the background and the small in-page notice invites the user
    // to confirm. Only Day 2+ requires a verified email.
    if (!user.email_verified && day > 1) { navigate("/story-week", { replace: true }); return; }
    if (!dayData) { navigate("/story-week"); return; }

    // CRITICAL: always reset day-specific state before loading. Prevents the
    // previous day's answer from carrying into the next day (audit bug).
    setText("");
    setPhotoPath(null);
    setAudioPath(null);
    setError("");
    setLocked(false);
    setDay5Choice(null);

    (async () => {
      try {
        const res = await api.get("/story_week/mine");
        const s = res.data;
        // Cache anon_id in localStorage so saveDay / transcribeVoice /
        // uploadPhoto (which all require it) work after a fresh registration
        // that arrives at Day 1 without ever visiting the anonymous flow.
        if (s?.anon_id) setAnonId(s.anon_id);
        // Backend authoritative gate — respect available_day
        if ((s?.available_day || 1) < day) {
          setLocked(true);
          setSession(s);
          return;
        }
        setSession(s);
        if (s?.story_mode) setStoryMode(s.story_mode);
        if (s?.storyteller_name) setName(s.storyteller_name);
        const existing = s?.days?.[String(day)];
        if (existing) {
          setText(existing.text || "");
          setPhotoPath(existing.photo_path || null);
          setAudioPath(existing.audio_path || null);
          if (day === 5) setDay5Choice(existing.gentle_variant ? "gentle" : "answer");
        }
      } catch (e) {
        // If session doesn't exist, send them to setup
        navigate("/story-week/setup", { replace: true });
      }
    })();
    // eslint-disable-next-line
  }, [day, user, authLoading]);

  // ---- Autosave draft every 5s and on visibility change / unload ----
  useEffect(() => {
    if (justSaved) return; // stop autosaving once explicitly saved
    if (locked) return;
    // reset baseline for change detection whenever the loaded text changes
    lastSavedTextRef.current = text || "";
    // eslint-disable-next-line
  }, [day, locked]);

  const flushDraft = React.useCallback(async () => {
    if (justSaved || locked) return;
    const current = (text || "").trim();
    if (!current) return;
    if (current === (lastSavedTextRef.current || "").trim()) return;
    setAutoState("saving");
    try {
      const extras = { audio_path: audioPath, photo_path: photoPath, status: "draft" };
      if (isDay5) extras.gentle_variant = day5Choice === "gentle";
      await saveDay(day, current, extras);
      lastSavedTextRef.current = text;
      setAutoState("saved");
    } catch (e) {
      setAutoState("error");
    }
  }, [text, audioPath, photoPath, day, isDay5, day5Choice, justSaved, locked]);

  // 5-second autosave loop while the user is typing.
  useEffect(() => {
    if (justSaved || locked) return;
    if (draftDebounceRef.current) clearTimeout(draftDebounceRef.current);
    draftDebounceRef.current = setTimeout(() => { flushDraft(); }, 5000);
    return () => { if (draftDebounceRef.current) clearTimeout(draftDebounceRef.current); };
  }, [text, flushDraft, justSaved, locked]);

  // On tab switch or unload, save immediately (best-effort).
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === "hidden") flushDraft(); };
    const onUnload = () => { flushDraft(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("pagehide", onUnload);
    window.addEventListener("beforeunload", onUnload);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pagehide", onUnload);
      window.removeEventListener("beforeunload", onUnload);
    };
  }, [flushDraft]);

  if (!dayData || authLoading || !user) return null;

  // Locked view — direct URL bypass attempt on a future day
  if (locked) {
    const nextAt = session?.next_unlock_at;
    return (
      <div className="min-h-screen paper flex items-center justify-center px-6" data-testid="sw-day-locked">
        <div className="max-w-md w-full text-center p-8 bg-white hairline-cream" style={{ borderRadius: 4 }}>
          <Lock className="mx-auto mb-4 text-[#B8973A]" size={28} aria-hidden="true" />
          <p className="chapter-roman mb-2">DAY {day}</p>
          <h1 className="text-3xl mb-3" style={{ fontFamily: "Newsreader, serif" }}>Not quite yet.</h1>
          <p className="text-[#1B2A4A]" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "16px", lineHeight: 1.7 }}>
            Story Week opens one question a day. Day {day} unlocks {nextAt ? `at ${new Date(nextAt).toLocaleString(undefined, { weekday: "long", hour: "2-digit", minute: "2-digit" })}` : "soon"}.
          </p>
          <button onClick={() => navigate(`/story-week/day/${session?.available_day || 1}`)} className="btn-gold mt-6" style={{ minHeight: 48 }} data-testid="sw-day-locked-back">
            Return to Day {session?.available_day || 1}
          </button>
        </div>
      </div>
    );
  }

  const isDay1 = day === 1;
  const needsName = isDay1 && !session?.storyteller_name;
  // Which question is currently in play (Day 5 has a gentle alternative)
  const questionText = isDay5 && day5Choice === "gentle" ? DAY5_GENTLE_QUESTION : dayData.question;

  const startRecording = async () => {
    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      mediaRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        stream.getTracks().forEach((t) => t.stop());
        setTranscribing(true);
        try {
          const res = await transcribeVoice(blob);
          setText((prev) => (prev ? prev.trim() + "\n\n" : "") + (res.text || ""));
          if (res.audio_path) setAudioPath(res.audio_path);
        } catch (e) {
          setError("Transcription failed. Please try again or type your answer.");
        } finally {
          setTranscribing(false);
        }
      };
      rec.start();
      setRecording(true);
    } catch (e) {
      setError("We couldn't access the microphone. Please allow mic access and try again.");
    }
  };

  const stopRecording = () => {
    if (mediaRef.current && mediaRef.current.state !== "inactive") {
      mediaRef.current.stop();
      setRecording(false);
    }
  };

  const onPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try {
      const res = await uploadPhoto(file);
      setPhotoPath(res.path);
    } catch (e2) {
      setError("Couldn't attach the photo. Try a smaller image?");
    }
  };

  const save = async () => {
    setError("");
    // Day 5 gentle path: skip is allowed without an answer
    if (isDay5 && day5Choice === "skip") {
      setSaving(true);
      try {
        await api.post("/story_week/skip", { day: 5, reason: "gentle" });
        if (day >= 7) navigate("/story-week/complete");
        else navigate(`/story-week/day/${day + 1}`);
      } catch (e) {
        setError("Couldn't save just now. Try again?");
      } finally {
        setSaving(false);
      }
      return;
    }
    if (isDay5 && !day5Choice) {
      setError("Please choose whether to answer, try a gentler question, or skip for now.");
      return;
    }
    if (!text.trim() && !audioPath) {
      setError("Take your time — please write or record your answer before continuing.");
      return;
    }
    setSaving(true);
    try {
      const extras = { audio_path: audioPath, photo_path: photoPath };
      if (isDay5) extras.gentle_variant = day5Choice === "gentle";
      const s = await saveDay(day, text.trim(), extras);
      setSession(s);
      setSavedNextUnlock(s?.next_unlock_at || null);
      setJustSaved(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.error === "day_locked") {
        setError(`This day isn't open yet. It unlocks ${detail.next_unlock_at ? "at " + new Date(detail.next_unlock_at).toLocaleString() : "soon"}.`);
      } else {
        setError(typeof detail === "string" ? detail : "Something went wrong. Try again?");
      }
    } finally {
      setSaving(false);
    }
  };

  if (justSaved) {
    const chapterName = STORY_WEEK_CHAPTERS[day];
    const nextDay = day + 1;
    const nextChapter = nextDay <= 7 ? STORY_WEEK_CHAPTERS[nextDay] : null;
    const timeStr = formatDeliveryTime(session?.delivery_time || "08:00");
    return (
      <div className="min-h-screen paper" data-testid={`story-week-day-${day}-saved`}>
        <header className="max-w-4xl mx-auto px-6 sm:px-10 py-8">
          <Link to="/" className="flex items-center gap-2 text-[#1B2A4A] hover:text-[#111D33] transition-colors" style={{ minHeight: 44, fontFamily: "'DM Sans', sans-serif", fontSize: "15px" }} data-testid="sw-saved-back-home">
            <ChevronLeft size={18} /> Back to The Living Book homepage
          </Link>
        </header>
        <main className="max-w-2xl mx-auto px-6 sm:px-10 py-10 sm:py-16 text-center">
          <p className="label-ui text-[10px] text-[#B8973A] tracking-[0.35em]" data-testid="sw-saved-chapter">{chapterName?.toUpperCase()}</p>
          <p className="mt-2 label-ui text-[11px] text-[#B8973A] tracking-[0.3em]">STORY WEEK &middot; DAY {day} OF 7 &middot; SaveD</p>
          <h1 className="mt-6 text-4xl sm:text-5xl" style={{ fontFamily: "Newsreader, serif", fontWeight: 300, lineHeight: 1.15 }} data-testid="sw-saved-headline">
            {day === 7
              ? <>That&rsquo;s all seven.<br/>Your Story Week book is ready.</>
              : <>Saved to your Living Book.</>}
          </h1>
          <Ornament className="my-8" />
          <p className="text-[#1B2A4A] mb-4" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "18px", lineHeight: 1.75 }} data-testid="sw-saved-body">
            {day === 1 && !user?.email_verified ? (
              <>Your answer is safely saved. Please confirm your email address so we can send you Question 2 tomorrow.</>
            ) : day === 7 ? (
              <>Well done &mdash; Story Week is complete. Your seven answers are safely stored in your Living Book.</>
            ) : (
              <>Your next question{stName ? ` for ${stName}` : ""} arrives tomorrow at <strong>{timeStr}</strong>{nextChapter ? <> &mdash; <span style={{ fontStyle: "italic" }}>{nextChapter}</span></> : ""}.</>
            )}
          </p>
          <div className="mt-6">
            <Link
              to="/my-book"
              className="label-ui text-[12px] text-[#B8973A] hover:text-[#111D33] transition-colors underline-offset-4 hover:underline"
              style={{ minHeight: 44, display: "inline-flex", alignItems: "center", letterSpacing: "0.02em" }}
              data-testid="sw-saved-see-book-link"
            >
              SEE MY BOOK SO FAR
            </Link>
          </div>
          {day === 1 && <Day1TimePreference />}
          <div className="mt-10 space-y-3 max-w-sm mx-auto">
            {day === 1 && !user?.email_verified && (
              <ResendConfirmationButton />
            )}
            <button
              type="button"
              onClick={() => navigate("/my-living-book")}
              className="btn-gold w-full"
              style={{ minHeight: 52, padding: "14px 20px", fontSize: "15px" }}
              data-testid="sw-saved-view-book"
            >
              Return to my Living Book
            </button>
          </div>
          {day === 1 && (
            <div className="mt-8 max-w-sm mx-auto">
              <ShareDay1Section />
            </div>
          )}
          <div className="mt-8 max-w-sm mx-auto">
            <Link
              to="/"
              className="block text-center py-3 label-ui text-[12px] text-[#8C8278] hover:text-[#111D33] transition-colors"
              style={{ minHeight: 48, lineHeight: "24px" }}
              data-testid="sw-saved-home-link"
            >
              Back to The Living Book homepage
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen paper" data-testid={`story-week-day-${day}`}>
      <header className="max-w-4xl mx-auto px-6 sm:px-10 py-8">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <button
            onClick={() => navigate("/my-living-book")}
            className="flex items-center gap-2 text-[#1B2A4A] hover:text-[#B8973A] transition-colors"
            style={{ minHeight: 44, fontFamily: "'DM Sans', sans-serif", fontSize: "15px" }}
            data-testid="sw-back-mlb"
          >
            <ChevronLeft size={18} /> Back to My Living Book
          </button>
          <div className="flex items-center gap-3">
            <span className="text-[#B8973A]" style={{ fontFamily: "Newsreader, serif" }}>✦</span>
            <span style={{ fontFamily: "Newsreader, serif", letterSpacing: "0.04em" }}>Story Week</span>
          </div>
        </div>
        <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
          <button
            onClick={() => day > 1 ? navigate(`/story-week/day/${day - 1}`) : navigate("/")}
            className="flex items-center gap-2 text-[#5C5548] hover:text-[#111D33] transition-colors"
            style={{ minHeight: 44, fontFamily: "'DM Sans', sans-serif", fontSize: "14px" }}
            data-testid="sw-back"
          >
            <ChevronLeft size={16} /> {day === 1 ? "Back to The Living Book homepage" : `Previous day`}
          </button>
        </div>
        <p className="text-center label-ui text-[11px] text-[#B8973A] tracking-[0.3em]" data-testid="sw-day-crumb">
          STORY WEEK · DAY {day} OF 7
        </p>
      </header>

      <main className="max-w-3xl mx-auto px-6 sm:px-10 py-10 sm:py-16">
        {/* Day 1 email-confirmation notice — non-blocking */}
        {day === 1 && user && !user.email_verified && (
          <div className="max-w-2xl mx-auto mb-10 p-5 sm:p-6" style={{ background: "#FFFDF8", border: "1px solid #E8D5A3", borderRadius: 4 }} data-testid="q1-email-notice">
            <p className="label-ui text-[11px] text-[#B8973A] tracking-[0.25em] mb-3">PLEASE CONFIRM YOUR EMAIL</p>
            <p className="text-[#1B2A4A] mb-4" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "16px", lineHeight: 1.7 }}>
              We have sent a confirmation link to <strong>{user.email}</strong>. Click the link so we can send your next Story Week question tomorrow.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1"><ResendConfirmationButton /></div>
              <button
                type="button"
                onClick={() => navigate("/login?flow=story_week&next=%2Fstory-week%2Fday%2F1")}
                className="py-3 px-4 hairline text-[#1B2A4A] flex-1"
                style={{ background: "white", borderColor: "#E8D5A3", borderRadius: 4, minHeight: 48, fontFamily: "'DM Sans', sans-serif", fontSize: "14px" }}
                data-testid="q1-correct-email-btn"
              >
                Click here to correct my email address
              </button>
            </div>
          </div>
        )}

        <div className="text-center">
          <p className="label-ui text-[10px] text-[#B8973A] tracking-[0.35em]" data-testid="sw-day-chapter">{(STORY_WEEK_CHAPTERS[day] || "").toUpperCase()}</p>
          <p className="mt-2 label-ui text-[11px] text-[#B8973A] tracking-[0.3em]" data-testid="sw-day-label">STORY WEEK &middot; DAY {day} OF 7</p>
          <h1 className="mt-6 text-4xl sm:text-6xl" style={{ fontFamily: "Newsreader, serif", fontWeight: 300, lineHeight: 1.1 }} data-testid="sw-day-title">
            {day === 1 ? <>Start your Living Book &mdash; Question 1</> : dayData.title}
          </h1>
          <Ornament className="my-8" />
        </div>

        {/* Opening reflection */}
        <div className="max-w-2xl mx-auto space-y-5 text-[#1B2A4A]" style={{ fontFamily: "Newsreader, serif", fontSize: "1.2rem", lineHeight: 1.75 }}>
          {dayData.opening.split("\n\n").map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>

        {/* The living question */}
        <div className="mt-14 mb-10 text-center max-w-2xl mx-auto">
          <p className="label-ui text-[10px] text-[#B8973A] tracking-[0.3em] mb-4">The living question</p>
          <p className="text-3xl sm:text-4xl italic text-[#111D33]" style={{ fontFamily: "Newsreader, serif", lineHeight: 1.3 }} data-testid="sw-day-question">
            "{questionText}"
          </p>
        </div>

        {/* PROMPT 4 PART 2 — How would you like to answer? */}
        {!locked && user?.storyteller && !user.storyteller.self_story && (
          <div className="max-w-2xl mx-auto mb-12" data-testid="sw-answer-mode">
            <p className="label-ui text-[10px] text-[#B8973A] tracking-[0.3em] mb-4 text-center">HOW WOULD YOU LIKE TO ANSWER?</p>
            <button
              type="button"
              onClick={() => navigate(`/story-week/day/${day}/conversation`)}
              className="w-full text-left bg-[#111D33] text-[#FAF7F2] hover:bg-[#1B2A4A] transition p-6 sm:p-7 hairline-cream mb-3"
              style={{ borderRadius: 4, minHeight: 108 }}
              data-testid="sw-mode-together-btn">
              <p className="label-ui text-[10px] text-[#B8973A] tracking-[0.3em]">RECOMMENDED</p>
              <p className="mt-1" style={{ fontFamily: "Newsreader, serif", fontSize: "1.6rem", lineHeight: 1.2 }}>
                Ask {stName || "them"} together
              </p>
              <p className="mt-1 text-[#E8DFC8]" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "14px", lineHeight: 1.5 }}>
                Sit down side by side. Press record. We turn your conversation into a chapter.
              </p>
            </button>
            <p className="text-center label-ui text-[10px] text-[#8C8278] tracking-[0.3em] my-4">— OR —</p>
            <p className="text-center text-[#5C5548]" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "14px" }}>
              They'll <strong>speak it</strong> or <strong>write it</strong> on their own using the tools below.
            </p>
          </div>
        )}

        {/* Day 5 — gentle-mode content warning + choices */}
        {isDay5 && (
          <div className="max-w-xl mx-auto mb-10 p-6" style={{ background: "#F7F0E0", borderLeft: "3px solid #B8973A", borderRadius: 2 }} data-testid="sw-day5-gentle">
            <p className="flex items-center gap-2 label-ui text-[10px] text-[#B8973A] tracking-[0.25em] mb-3">
              <HeartHandshake size={14} /> A GENTLE NOTE
            </p>
            <p className="text-[#1B2A4A] mb-5" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "16px", lineHeight: 1.7 }}>
              Some memories can feel difficult. Share only what feels right today.
            </p>
            <div className="grid gap-2">
              {[
                { v: "answer", l: "Answer this question" },
                { v: "gentle", l: "Choose a gentler question" },
                { v: "skip", l: "Skip for now" },
              ].map((opt) => (
                <label
                  key={opt.v}
                  className="flex items-center gap-3 px-4 py-3 hairline-cream bg-white cursor-pointer"
                  style={{ borderRadius: 4, borderColor: day5Choice === opt.v ? "#B8973A" : undefined, minHeight: 48 }}
                  data-testid={`sw-day5-${opt.v}-label`}
                >
                  <input
                    type="radio"
                    name="day5-choice"
                    value={opt.v}
                    checked={day5Choice === opt.v}
                    onChange={() => setDay5Choice(opt.v)}
                    data-testid={`sw-day5-${opt.v}`}
                  />
                  <span className="text-[#1B2A4A]" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "16px" }}>{opt.l}</span>
                </label>
              ))}
            </div>
            <p className="mt-4 text-sm text-[#8C8278]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Need support? Speak to Samaritans on <a href="tel:116123" className="underline text-[#B8973A]">116 123</a> (free, 24 hours).
            </p>
          </div>
        )}

        {/* Day 1 — capture email + name */}
        {isDay1 && needsName && (
          <div className="max-w-xl mx-auto mb-8">
            <div>
              <label htmlFor="sw-name" className="block mb-2 text-[#1B2A4A]" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "14px", fontWeight: 500 }}>
                {storyMode === "helping" ? "Whose story are you helping to tell?" : "Your name"}
              </label>
              <input
                id="sw-name"
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={storyMode === "helping" ? "e.g. Grandma Rose, my Dad, Uncle Frank" : "Your name"}
                className="w-full px-5 py-4 hairline-cream bg-white text-[#1B2A4A]"
                style={{ borderRadius: 4, fontFamily: "'DM Sans', sans-serif", fontSize: "16px", minHeight: 48 }}
                data-testid="sw-storyteller-name"
              />
            </div>
          </div>
        )}

        {/* Answer textarea */}
        <div className="max-w-2xl mx-auto">
          {/* Recording tips — collapsible, visible above voice controls */}
          <div className="mb-4">
            <RecordingTips variant="toggle" />
          </div>
          <label
            htmlFor="sw-textarea"
            className="block mb-2 text-[#1B2A4A]"
            style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "15px", fontWeight: 500 }}
          >
            Write your answer
          </label>
          <textarea
            id="sw-textarea"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Begin wherever the memory takes you…"
            className="w-full px-5 py-5 hairline-cream bg-white resize-none"
            rows={9}
            style={{ borderRadius: 4, fontFamily: "'DM Sans', sans-serif", fontSize: "17px", lineHeight: 1.7, minHeight: 220 }}
            data-testid="sw-textarea"
          />

          {/* Voice + Photo controls */}
          <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              {!recording ? (
                <button type="button" onClick={startRecording} disabled={transcribing} className="btn-outline-navy flex items-center gap-2" data-testid="sw-record-start">
                  <Mic size={16} /> {transcribing ? "Transcribing…" : "Record"}
                </button>
              ) : (
                <button type="button" onClick={stopRecording} className="btn-outline-navy flex items-center gap-2" style={{ color: "#B8973A", borderColor: "#B8973A" }} data-testid="sw-record-stop">
                  <StopCircle size={16} /> Stop
                </button>
              )}
              <input type="file" ref={fileInputRef} accept="image/*" onChange={onPhoto} className="hidden" data-testid="sw-photo-input" />
              <button type="button" onClick={() => fileInputRef.current?.click()} className="btn-outline-navy flex items-center gap-2" data-testid="sw-photo-btn">
                <ImagePlus size={16} /> {photoPath ? "Replace photo" : "Add a photo"}
              </button>
              {audioPath && !recording && !transcribing && (
                <span className="label-ui text-[10px] text-[#B8973A]" data-testid="sw-audio-saved">✦ Voice saved</span>
              )}
            </div>
          </div>

          {photoPath && (
            <p className="mt-3 label-ui text-[10px] text-[#8C8278]">✦ Photo attached</p>
          )}
        </div>

        {/* Gentle invitations */}
        <div className="max-w-2xl mx-auto mt-12">
          <button
            type="button"
            onClick={() => setShowHelpers((v) => !v)}
            className="label-ui text-[11px] text-[#8C8278] hover:text-[#B8973A] transition-colors"
            data-testid="sw-helpers-toggle"
          >
            {showHelpers ? "Hide" : "If it helps…"}
          </button>
          {showHelpers && (
            <ul className="mt-4 space-y-2.5 text-[#8C8278] italic" style={{ fontFamily: "Newsreader, serif", fontSize: "1.15rem" }}>
              {dayData.ifItHelps.map((p, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="text-[#B8973A] mt-1.5">·</span><span>{p}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Save */}
        {error && <p className="max-w-2xl mx-auto mt-6 text-red-700" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "15px", textAlign: "center" }} role="alert" data-testid="sw-error">{error}</p>}
        <div className="max-w-2xl mx-auto mt-10 text-center">
          <button
            onClick={save}
            disabled={saving || transcribing}
            className="btn-primary text-lg px-10 py-4"
            style={{ minHeight: 52, fontSize: "15px" }}
            data-testid="sw-save-btn"
          >
            {saving
              ? <><Loader2 size={16} className="inline animate-spin mr-2" /> Saving…</>
              : `Click here to save Day ${day === 1 ? "One" : day === 2 ? "Two" : day === 3 ? "Three" : day === 4 ? "Four" : day === 5 ? "Five" : day === 6 ? "Six" : "Seven"} to my Living Book`}
          </button>
          <p className="mt-3 label-ui text-[11px] text-[#8C8278] tracking-[0.2em]" data-testid="sw-autosave-status" aria-live="polite">
            {autoState === "saving" ? "Saving…" : autoState === "saved" ? "Draft saved" : autoState === "error" ? "Couldn't autosave — your Save button still works" : "\u00A0"}
          </p>
        </div>

        {/* Quiet thought */}
        <div className="mt-16 text-center">
          <Ornament />
          <p className="mt-8 text-[#8C8278] italic" style={{ fontFamily: "Newsreader, serif", fontSize: "1.35rem" }}>
            {dayData.quiet}
          </p>
        </div>

        <p className="mt-14 text-center label-ui text-[10px] text-[#B8973A]/70 tracking-[0.3em]">
          {day} · {day + 1 <= 7 ? `Tomorrow: ${STORY_WEEK_DAYS[day].title}` : "Tomorrow: your Living Book"}
        </p>
      </main>
    </div>
  );
}
